// Shared by the manager, partner and vendor permission upload flows, which post
// to three endpoints answering with the same status codes and the same error body.
//
// `upload()` in apiClient is a bare fetch and fetch does not reject on 4xx/5xx,
// so callers must inspect the status themselves or every failure is reported to
// the user as a successful upload.

const validationReasons: { [type: string]: string } = {
    'non-ascii-file-name': 'its name may only contain plain Latin letters, digits and basic punctuation',
    'empty-file': 'the file is empty',
    'no-file': 'no file was attached',
};

// Own lookup, because `validationReasons[type]` alone answers a type such as
// `constructor` or `toString` with a member of Object.prototype, and `??` only
// guards against null and undefined -- the function would then read a piece of
// the prototype into the sentence shown to the user.
const validationReasonFor = (type: string): string | undefined =>
    Object.prototype.hasOwnProperty.call(validationReasons, type) ? validationReasons[type] : undefined;

// ExceededFilesizeLimitationException, kept in sync with
// shared/src/file-upload-limitation/exceptions/exceeded-filesize-limitation.exception.ts.
// Not imported: www does not build against shared.
const dailyUploadLimitType = 'daily-upload-limit';

interface UploadErrorBody {
    type?: string;
}

async function readErrorType(response: Response): Promise<string> {
    try {
        const body: UploadErrorBody = (await response.json()) ?? {};
        return body.type ?? '';
    } catch {
        // FileExtensionValidationInterceptor answers 422 with an empty body, and
        // so does the 413 raised by multer.
        return '';
    }
}

// PermissionFileValidationInterceptor runs ahead of the file type check, so a 422
// without a type can now only be the type check itself -- a missing or empty file
// carries its own type and no longer reads as an unsupported type.
const unsupportedTypeReason = 'its type is not supported (only PDF, JPG and PNG are allowed)';

export const uploadSuccessMessage = (fileName: string): string => `${fileName} was uploaded successfully`;

export const uploadErrorMessage = (fileName: string): string => `${fileName} could not be uploaded`;

export async function uploadFailureMessage(response: Response, fileName: string): Promise<string> {
    if (response.status === 413) {
        return `${fileName} could not be uploaded because it is too large`;
    }

    if (response.status === 502) {
        return `${fileName} could not be uploaded right now, please try again`;
    }

    if (response.status === 403) {
        // Two sources answer 403 on these endpoints. ExceededFilesizeLimitationException
        // says so with its own type; ManagerFeaturesGuard answers the same status when
        // the manager no longer holds the feature -- which the button, rendered from the
        // auth state loaded earlier, does not know about yet. Reporting that as an
        // exhausted limit sends the user to wait for a reset that would not help.
        if ((await readErrorType(response)) === dailyUploadLimitType) {
            // The server message quotes the exact megabytes used, but it is a server
            // message and is not echoed.
            return `${fileName} could not be uploaded because the daily upload limit has been reached`;
        }

        return `${fileName} could not be uploaded because you are not allowed to upload it, please reload the page`;
    }

    if (response.status === 422) {
        // Only messages we author are shown; the raw server message is never
        // echoed, so framework wording (e.g. a 400 from ParseIntPipe) cannot leak
        // to the user.
        const reason = validationReasonFor(await readErrorType(response)) ?? unsupportedTypeReason;

        return `${fileName} could not be uploaded because ${reason}`;
    }

    return uploadErrorMessage(fileName);
}
